import { SEO } from '../../components/seo/SEO';
import { Link } from 'react-router-dom';
import { getBaseUrl } from '../../lib/utils';

const pillars = [
  {
    icon: 'ri-user-heart-line',
    label: '実体験',
    title: '試していないことは書かない',
    desc: '実際に触って、作って、詰まって、直した経験だけを書く。「使えそう」という推測ではなく、「使った結果」を残す。',
  },
  {
    icon: 'ri-repeat-line',
    label: '再現性',
    title: '読んだ人が次に動けること',
    desc: '「なんとなくすごそう」で終わらせない。手順・判断・理由を整理して、読んだ人が自分でも試せる形にする。',
  },
  {
    icon: 'ri-file-list-2-line',
    label: '仕様化',
    title: '再利用できる形に落とし込む',
    desc: '一度やったことを次に活かせるよう、プロセスをテンプレートや仕様書の形に整理する。体験談で終わらせない。',
  },
  {
    icon: 'ri-scales-line',
    label: '比較して考える',
    title: 'ツールを役割で使い分ける',
    desc: '「これが最強」ではなく、「この用途ならこれ」という使い分けを大事にする。比較と検証を記録として残す。',
  },
];

const forWhom = [
  'AIを使って何か作りたい・作っている人',
  '個人開発者・フリーランス・制作者',
  '業務にAIを取り入れたい人',
  'UI再現や制作フローを知りたい人',
  '「紹介記事」ではなく「実践記録」を求めている人',
];

const howArticles = [
  { icon: 'ri-tools-line', text: '実際に触ったツール・フローをベースにする' },
  { icon: 'ri-bug-line', text: '詰まった点・失敗・やり直しも含めて書く' },
  { icon: 'ri-file-copy-line', text: '読者が真似できるように手順を整理する' },
  { icon: 'ri-code-line', text: '必要ならプロンプトや仕様レベルまで落とす' },
  { icon: 'ri-flag-line', text: '体験談だけで終わらず、使い方に着地させる' },
];

export default function AboutPage() {
  const baseUrl = getBaseUrl();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About | つくってなんぼ',
    url: `${baseUrl}/about`,
    description: 'AIを使って作る・整える・速くする実践メディア「つくってなんぼ」のAboutページ。サイトの思想・方針・記事の作り方を紹介。',
  };

  return (
    <>
      <SEO
        title="About | つくってなんぼ — AI実践メディア"
        description="AIを使って作る・整える・速くする実践メディア「つくってなんぼ」について。サイトの目的・思想・記事の作り方・想定読者を紹介します。"
        canonical="/about"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#FDFAF6]">

        {/* ── Page header ── */}
        <div className="pt-24 sm:pt-32 pb-10 border-b border-[#E8E0D5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-3"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              About
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#2C2416] mb-5 leading-snug"
              style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif", letterSpacing: '-0.01em' }}
            >
              このサイトについて
            </h1>
            <p
              className="text-base text-[#4A3F35] leading-relaxed"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              AIツールを紹介して終わる場所ではありません。<br />
              実際に使って何を作れたか、どこで詰まり、どう直したかまでを残す——<br />
              それが「つくってなんぼ」の役割です。
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6">

          {/* ── Section 1: What this site does ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              このサイトは何をする場所か
            </p>
            <div className="space-y-4 text-sm text-[#4A3F35] leading-[2]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              <p>
                AIツールの記事は世の中にたくさんあります。でも多くは「こんなことができます」「こんな使い方があります」で終わっています。
              </p>
              <p>
                このサイトはそこで終わりません。
              </p>
              <p>
                実際に使って何を作れたか。どこで詰まったか。どう直したか。その過程を、<strong className="text-[#2C2416] font-semibold">読んだ人が自分でも再現できる形</strong>に整理して残します。
              </p>
              <p>
                扱うテーマは、AIツール活用・UI再現・制作フロー・バイブコーディング・仕様書化・比較検証など。どれも「実際に手を動かした記録」です。
              </p>
            </div>
          </section>

          {/* ── Section 2: Why ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              なぜこのサイトをやっているのか
            </p>
            <div className="space-y-4 text-sm text-[#4A3F35] leading-[2]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              <p>
                AIを使い始めたとき、「すごそう」という感覚はあっても、「自分の仕事にどう使うか」がわからなかった。
              </p>
              <p>
                紹介記事を読んでも、実際に試してみると詰まる。詰まったときに参考になる記事がない。あっても、自分の状況に合わせて読み替えるのが難しい。
              </p>
              <p>
                だから、<strong className="text-[#2C2416] font-semibold">自分が実際に詰まったところと、どう解決したかを残す</strong>ことにしました。
              </p>
              <p>
                「なんとなくすごそう」ではなく、「この手順なら自分でもできる」「この考え方なら試せる」——そう思ってもらえる記事を作ることが、このサイトの目的です。
              </p>
            </div>

            {/* Pull quote */}
            <div className="mt-7 border-l-2 border-[#5B7B6F]/40 pl-4">
              <p
                className="text-base text-[#4A3F35] leading-relaxed"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                「再現できる形で整理する」ことが、<br />
                このサイトの一番の仕事だと思っています。
              </p>
            </div>
          </section>

          {/* ── Section 3: Pillars ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              このサイトで大事にしていること
            </p>
            <div className="space-y-0">
              {pillars.map((pillar, index) => (
                <div key={pillar.label}>
                  <div className="flex items-start gap-4 py-5">
                    <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#F0EBE3] rounded-md">
                      <i className={`${pillar.icon} text-sm text-[#5B7B6F]`}></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span
                          className="text-xs font-semibold text-[#5B7B6F] tracking-wide"
                          style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                        >
                          {pillar.label}
                        </span>
                      </div>
                      <h3
                        className="text-sm font-bold text-[#2C2416] mb-1.5"
                        style={{ fontFamily: "'Noto Serif JP', serif" }}
                      >
                        {pillar.title}
                      </h3>
                      <p
                        className="text-sm text-[#7A6E62] leading-relaxed"
                        style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                      >
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                  {index < pillars.length - 1 && <div className="border-t border-[#EDE7DF]"></div>}
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 4: For whom ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              どんな人向けのサイトか
            </p>
            <ul className="space-y-3">
              {forWhom.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-[#5B7B6F] text-base flex-shrink-0 mt-0.5"></i>
                  <span
                    className="text-sm text-[#4A3F35] leading-relaxed"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="text-sm text-[#7A6E62] leading-relaxed mt-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              逆に、「最新AIニュースをまとめて知りたい」「ツールのスペックを比較したい」という方には向いていないかもしれません。このサイトは、実践と記録に特化しています。
            </p>
          </section>

          {/* ── Section 5: How articles are made ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              記事の作り方
            </p>
            <p
              className="text-sm text-[#4A3F35] leading-relaxed mb-6"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              記事は、実際に手を動かした経験をベースに作っています。以下の方針で書いています。
            </p>
            <div className="bg-[#F8F3EC] border border-[#E8E0D5] rounded-lg p-5 space-y-3">
              {howArticles.map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-[#5B7B6F] text-sm`}></i>
                  </div>
                  <span
                    className="text-sm text-[#4A3F35] leading-relaxed"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
            <p
              className="text-sm text-[#7A6E62] leading-relaxed mt-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              完璧な記事より、正直な記録を優先します。詰まったことや失敗も含めて書くのは、それが読者にとって一番役立つと思っているからです。
            </p>
          </section>

          {/* ── Section 6: About the author ── */}
          <section className="py-10 border-b border-[#EDE7DF]">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              運営者について
            </p>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-[#F0EBE3] rounded-full border border-[#E8E0D5]">
                <i className="ri-user-line text-lg text-[#9A8E84]"></i>
              </div>
              <div className="flex-1">
                <p
                  className="text-sm font-semibold text-[#2C2416] mb-1"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  つくってなんぼ 編集部
                </p>
                <p
                  className="text-xs text-[#9A8E84] mb-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  個人運営 / AI実践メディア
                </p>
                <p
                  className="text-sm text-[#7A6E62] leading-relaxed"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  AIツールを使って何かを作ることに興味があり、その過程を記録しています。制作・開発・ライティングを一人でやっています。
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    <i className="ri-twitter-x-line"></i>
                    X (Twitter)
                  </a>
                  <span className="text-[#D4C9BE]">·</span>
                  <a
                    href="https://github.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    <i className="ri-github-line"></i>
                    GitHub
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* ── Section 7: Links ── */}
          <section className="py-10">
            <p
              className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              このサイトを読む
            </p>
            <div className="space-y-3">
              <Link
                to="/articles"
                className="flex items-center justify-between p-4 border border-[#E8E0D5] rounded-lg hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#F0EBE3] rounded-md">
                    <i className="ri-article-line text-sm text-[#5B7B6F]"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2416] group-hover:text-[#5B7B6F] transition-colors" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      記事一覧
                    </p>
                    <p className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      全記事をまとめて見る
                    </p>
                  </div>
                </div>
                <i className="ri-arrow-right-line text-[#C8BFB5] group-hover:text-[#5B7B6F] transition-colors"></i>
              </Link>

              <Link
                to="/series"
                className="flex items-center justify-between p-4 border border-[#E8E0D5] rounded-lg hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#F0EBE3] rounded-md">
                    <i className="ri-list-ordered text-sm text-[#5B7B6F]"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2416] group-hover:text-[#5B7B6F] transition-colors" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      シリーズ一覧
                    </p>
                    <p className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      進行中の制作シリーズを追う
                    </p>
                  </div>
                </div>
                <i className="ri-arrow-right-line text-[#C8BFB5] group-hover:text-[#5B7B6F] transition-colors"></i>
              </Link>

              <Link
                to="/contact"
                className="flex items-center justify-between p-4 border border-[#E8E0D5] rounded-lg hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#F0EBE3] rounded-md">
                    <i className="ri-mail-line text-sm text-[#5B7B6F]"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2416] group-hover:text-[#5B7B6F] transition-colors" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      お問い合わせ
                    </p>
                    <p className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      フィードバック・ご意見はこちら
                    </p>
                  </div>
                </div>
                <i className="ri-arrow-right-line text-[#C8BFB5] group-hover:text-[#5B7B6F] transition-colors"></i>
              </Link>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}
